const ADMIN_USERS_STORAGE_KEY = "managed-admin-users-v1"

const safeRead = (key, fallback) => {
    if (typeof window === "undefined") return fallback

    try {
        const rawValue = window.localStorage.getItem(key)
        return rawValue ? JSON.parse(rawValue) : fallback
    } catch (error) {
        return fallback
    }
}

const safeWrite = (key, value) => {
    if (typeof window === "undefined") return

    try {
        window.localStorage.setItem(key, JSON.stringify(value))
    } catch (error) {
        // ignore local storage write failures
    }
}

const normalizeText = (value) => (value || "").toString().trim()

const toSlug = (value) =>
    normalizeText(value)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ".")
        .replace(/^\.+|\.+$/g, "")

export const defaultAdminPermissions = [
    { key: "users", label: "Users" },
    { key: "plans", label: "Plans" },
    { key: "reports", label: "Reports" },
    { key: "enquiries", label: "Enquiries" },
    { key: "professions", label: "Professions" },
    { key: "payments", label: "Payments" },
]

const createPermissionState = (input = {}) =>
    defaultAdminPermissions.reduce((acc, permission) => {
        acc[permission.key] = Boolean(input?.[permission.key])
        return acc
    }, {})

const decodeTokenPayload = (token) => {
    try {
        if (!token || token.split(".").length < 2) return {}
        return JSON.parse(window.atob(token.split(".")[1]))
    } catch (error) {
        return {}
    }
}

export const getCurrentGlobalAdmin = () => {
    const tokenPayload = decodeTokenPayload(typeof window !== "undefined" ? window.localStorage.getItem("token") : "")
    const storedUser = safeRead("userDetails", {})
    const currentUser = Object.keys(storedUser || {}).length ? storedUser : tokenPayload

    return {
        id: currentUser?._id || currentUser?.id || currentUser?.userId || currentUser?.sub || "global-admin",
        name:
            currentUser?.name ||
            currentUser?.fullName ||
            [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(" ") ||
            currentUser?.userName ||
            "Global Admin",
        userName: currentUser?.userName || currentUser?.email || "global.admin",
        email: currentUser?.email || "admin@royalkavery.com",
        role: currentUser?.role || "Super_Admin",
    }
}

const getStoredAdmins = () => safeRead(ADMIN_USERS_STORAGE_KEY, [])

const saveAdmins = (admins) => {
    const nextAdmins = [...admins].sort((left, right) => {
        if ((left?.status || "active") !== (right?.status || "active")) {
            return (left?.status || "active") === "active" ? -1 : 1
        }

        return normalizeText(left?.fullName).localeCompare(normalizeText(right?.fullName))
    })

    safeWrite(ADMIN_USERS_STORAGE_KEY, nextAdmins)
    return nextAdmins
}

export const getManagedAdmins = () => getStoredAdmins()

export const createAdminDraft = () => ({
    fullName: "",
    userName: "",
    email: "",
    phone: "",
    accessLevel: "Operations Admin",
    status: "active",
    notes: "",
    permissions: createPermissionState({
        users: true,
        enquiries: true,
    }),
})

const buildUsername = (draft) => {
    if (normalizeText(draft?.userName)) return normalizeText(draft.userName)
    return toSlug(draft?.fullName) || `admin.${Date.now()}`
}

const generateTempPassword = () =>
    `RK${Math.random().toString(36).slice(2, 6).toUpperCase()}${Date.now().toString().slice(-4)}`

export const upsertManagedAdmin = (draft, adminId = null) => {
    const admins = getStoredAdmins()
    const now = new Date().toISOString()
    const normalizedDraft = {
        fullName: normalizeText(draft?.fullName),
        userName: buildUsername(draft),
        email: normalizeText(draft?.email),
        phone: normalizeText(draft?.phone),
        accessLevel: normalizeText(draft?.accessLevel) || "Operations Admin",
        status: draft?.status === "inactive" ? "inactive" : "active",
        notes: normalizeText(draft?.notes),
        permissions: createPermissionState(draft?.permissions),
    }

    if (adminId) {
        const nextAdmins = admins.map((admin) =>
            admin.id === adminId
                ? {
                    ...admin,
                    ...normalizedDraft,
                    updatedAt: now,
                }
                : admin
        )

        return saveAdmins(nextAdmins)
    }

    const nextAdmin = {
        id: `admin-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        ...normalizedDraft,
        tempPassword: generateTempPassword(),
        createdAt: now,
        updatedAt: now,
        passwordResetAt: now,
    }

    return saveAdmins([...admins, nextAdmin])
}

export const resetManagedAdminPassword = (adminId, password) => {
    const now = new Date().toISOString()
    const nextPassword = normalizeText(password) || generateTempPassword()

    const nextAdmins = getStoredAdmins().map((admin) =>
        admin.id === adminId
            ? {
                ...admin,
                tempPassword: nextPassword,
                passwordResetAt: now,
                updatedAt: now,
            }
            : admin
    )

    return {
        admins: saveAdmins(nextAdmins),
        password: nextPassword,
    }
}

export const toggleManagedAdminStatus = (adminId) => {
    const now = new Date().toISOString()
    const nextAdmins = getStoredAdmins().map((admin) =>
        admin.id === adminId
            ? {
                ...admin,
                status: admin.status === "inactive" ? "active" : "inactive",
                updatedAt: now,
            }
            : admin
    )

    return saveAdmins(nextAdmins)
}

export const removeManagedAdmin = (adminId) => saveAdmins(getStoredAdmins().filter((admin) => admin.id !== adminId))

